import {useCallback, useRef, useSyncExternalStore} from 'react';
import type {Editor} from 'grapesjs';

export interface EditorUndoRedoState {
  canUndo: boolean;
  canRedo: boolean;
}

// GrapesJS events that fire whenever the undo/redo stack changes.
const UNDO_REDO_EVENTS = 'undo redo component:create component:update component:remove change:changesCount';

const INITIAL_STATE: EditorUndoRedoState = {canUndo: false, canRedo: false};

/**
 * Subscribes to GrapesJS undo/redo stack changes and returns stable boolean flags.
 * Skips re-renders when neither flag changes (snapshot identity is preserved).
 *
 * @param editor - GrapesJS editor instance, or null before initialisation.
 * @param checkCanUndo - predicate that reads canUndo from the editor.
 * @param checkCanRedo - predicate that reads canRedo from the editor.
 */
export function useEditorUndoRedoState(editor: Editor | null, checkCanUndo: (editor: Editor) => boolean, checkCanRedo: (editor: Editor) => boolean): EditorUndoRedoState {
  const lastSnapshotRef = useRef<EditorUndoRedoState>(INITIAL_STATE);

  const subscribe = useCallback(
    (onChange: () => void): (() => void) => {
      if (!editor) {
        return noopCleanup;
      }
      editor.on(UNDO_REDO_EVENTS, onChange);
      return (): void => {
        editor.off(UNDO_REDO_EVENTS, onChange);
      };
    },
    [editor]
  );

  const getSnapshot = useCallback((): EditorUndoRedoState => {
    const nextUndo = editor ? checkCanUndo(editor) : false;
    const nextRedo = editor ? checkCanRedo(editor) : false;
    const last = lastSnapshotRef.current;
    if (nextUndo === last.canUndo && nextRedo === last.canRedo) {
      return last;
    }
    lastSnapshotRef.current = {canUndo: nextUndo, canRedo: nextRedo};
    return lastSnapshotRef.current;
  }, [editor, checkCanUndo, checkCanRedo]);

  return useSyncExternalStore(subscribe, getSnapshot, getInitialState);
}

function getInitialState(): EditorUndoRedoState {
  return INITIAL_STATE;
}

function noopCleanup(): void {}
